import { useEffect, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  TextInput,
  View,
} from "react-native";
import {
  CalendarDays,
  FileText,
  Hash,
  MapPin,
  Snowflake,
  Shield,
} from "lucide-react-native";
import {
  Dialog,
  DialogHeader,
  DialogPrimaryButton,
  DialogSecondaryButton,
  MoonDateField,
  MoonFieldInput,
  MoonFormField,
} from "@louise/moon-ui";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import {
  type StorageLocation,
  type StorageLocationPatch,
  useUpdateOocyteCohort,
  useUpdateSpecimen,
} from "@src/packages/care/cryovault/useSpecimens";
import {
  parseStorageLocationMeta,
  parseTankRef,
  serializeStorageLocationMeta,
} from "@src/packages/care/components/frozen-storage-data";
import { EMBER } from "../../onboarding/theme";
import type { CryoSpecimenDetail } from "./CryoSpecimenDetailModal";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
export type CryoSpecimenEditField =
  | "status"
  | "notes"
  | "sample_id"
  | "identification_number"
  | "collection_date"
  | "freeze_date"
  | "facility"
  | "storage_locator";

const STATUS_OPTIONS = [
  "collected",
  "processed",
  "stored",
  "used",
  "discarded",
  "donated",
  "in_transit",
] as const;

type EditableStatus = (typeof STATUS_OPTIONS)[number];

function getStatusLabel(t: TranslationFunction, value: EditableStatus): string {
  return t(`cryo_vault.modal.status_value_${value}`);
}

function formatStorageLocator(specimen: CryoSpecimenDetail): string {
  const parts = [
    specimen.tankNumber ? `Tank ${specimen.tankNumber}` : null,
    specimen.canisterNumber ? `Canister ${specimen.canisterNumber}` : null,
    specimen.caneNumber ? `Cane ${specimen.caneNumber}` : null,
    specimen.subcanisterNumber
      ? `Subcanister ${specimen.subcanisterNumber}`
      : null,
    specimen.rackNumber ? `Rack ${specimen.rackNumber}` : null,
    specimen.strawNumber ? `Straw ${specimen.strawNumber}` : null,
  ].filter((part): part is string => Boolean(part));
  return parts.join(", ") || specimen.storageId || "";
}

function replaceSerializedField(
  rawNotes: string | null | undefined,
  fields: readonly { label: string; value: string }[],
  userNotes: string,
): string | null {
  const labels = new Set(fields.map(({ label }) => label.toLowerCase()));
  const lines = (rawNotes ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => {
      const match = line.match(/^([^:]+):\s*/);
      return !match || !labels.has(match[1]!.trim().toLowerCase());
    })
    .filter((line) => !/^Notes:\s*/i.test(line));

  for (const field of fields) {
    if (field.value.trim()) lines.push(`${field.label}: ${field.value.trim()}`);
  }

  const trimmedNotes = userNotes.trim();
  if (trimmedNotes) lines.push(`Notes: ${trimmedNotes}`);
  return lines.length > 0 ? lines.join("\n") : null;
}

function buildUpdatedStorageLocations(
  specimen: CryoSpecimenDetail,
  field: CryoSpecimenEditField,
  value: string,
): StorageLocationPatch[] {
  const existingLocations = specimen.storageLocations ?? [];
  const activeIndex = existingLocations.findIndex(
    (location) => location.isActive && location.storageType === "frozen",
  );
  const activeLocation =
    activeIndex >= 0 ? existingLocations[activeIndex] : undefined;
  const facility =
    field === "facility" ? value.trim() : specimen.facility.trim();
  const freezeDate =
    field === "freeze_date"
      ? value.trim()
      : specimen.frozenDate?.trim() ||
        activeLocation?.storedFrom ||
        specimen.collectedAt?.trim() ||
        "";
  const storageLocator =
    field === "storage_locator"
      ? value.trim()
      : formatStorageLocator(specimen).trim();

  if (!facility || !freezeDate) {
    throw new Error("A facility and freeze date are required");
  }

  const parsedLocator = parseTankRef(storageLocator);
  const storageVessel =
    activeLocation?.storageVessel === "straw" ||
    activeLocation?.storageVessel === "vial"
      ? activeLocation.storageVessel
      : null;
  const strawColor =
    activeLocation?.strawColor === "blue" ||
    activeLocation?.strawColor === "yellow" ||
    activeLocation?.strawColor === "white" ||
    activeLocation?.strawColor === "red" ||
    activeLocation?.strawColor === "green" ||
    activeLocation?.strawColor === "purple"
      ? activeLocation.strawColor
      : null;
  const brand =
    activeLocation?.brand === "kitazato" ||
    activeLocation?.brand === "cooper_surgical" ||
    activeLocation?.brand === "vitrolife" ||
    activeLocation?.brand === "cryotech"
      ? activeLocation.brand
      : null;
  const vitrificationSystem =
    activeLocation?.vitrificationSystem === "open" ||
    activeLocation?.vitrificationSystem === "closed"
      ? activeLocation.vitrificationSystem
      : null;
  const quantityUnit =
    activeLocation?.quantityUnit === "count" ||
    activeLocation?.quantityUnit === "milliliter" ||
    activeLocation?.quantityUnit === "microliter" ||
    activeLocation?.quantityUnit === "straw" ||
    activeLocation?.quantityUnit === "vial"
      ? activeLocation.quantityUnit
      : undefined;
  const updatedLocation: StorageLocationPatch = {
    siteEntityId: activeLocation?.siteEntityId ?? undefined,
    custodianEntityId: activeLocation?.custodianEntityId ?? undefined,
    storageAccountId: activeLocation?.storageAccountId ?? undefined,
    quantity: activeLocation?.quantity,
    quantityUnit,
    storedFrom: freezeDate,
    storedTo: activeLocation?.storedTo ?? null,
    isActive: true,
    facility,
    storageType: "frozen",
    storageVessel,
    tankNumber: parsedLocator.tankNumber ?? activeLocation?.tankNumber ?? null,
    canisterNumber:
      parsedLocator.canisterNumber ?? activeLocation?.canisterNumber ?? null,
    caneNumber: parsedLocator.caneNumber ?? activeLocation?.caneNumber ?? null,
    subcanisterNumber:
      parsedLocator.subcanisterNumber ??
      activeLocation?.subcanisterNumber ??
      null,
    rackNumber: parsedLocator.rackNumber ?? activeLocation?.rackNumber ?? null,
    strawColor,
    strawNumber:
      parsedLocator.strawNumber ?? activeLocation?.strawNumber ?? null,
    brand,
    vitrificationSystem,
    incubatorNumber: activeLocation?.incubatorNumber ?? null,
    chamberNumber: activeLocation?.chamberNumber ?? null,
    dishNumber: activeLocation?.dishNumber ?? null,
    dropNumber: activeLocation?.dropNumber ?? null,
    reason: activeLocation?.reason ?? null,
    notes:
      serializeStorageLocationMeta({
        ...parseStorageLocationMeta(activeLocation?.notes),
        storageId: parsedLocator.notes,
      }) ??
      activeLocation?.notes ??
      null,
  };

  const requestLocations = existingLocations.map(toStorageLocationPatch);
  if (activeIndex < 0) return [...requestLocations, updatedLocation];
  return existingLocations.map((location, index) =>
    index === activeIndex
      ? { ...updatedLocation, id: location.id }
      : toStorageLocationPatch(location),
  );
}

function toStorageLocationPatch(
  location: StorageLocation,
): StorageLocationPatch {
  const quantityUnit =
    location.quantityUnit === "count" ||
    location.quantityUnit === "milliliter" ||
    location.quantityUnit === "microliter" ||
    location.quantityUnit === "straw" ||
    location.quantityUnit === "vial"
      ? location.quantityUnit
      : undefined;
  const storageType =
    location.storageType === "frozen" || location.storageType === "incubator"
      ? location.storageType
      : undefined;
  const storageVessel =
    location.storageVessel === "straw" || location.storageVessel === "vial"
      ? location.storageVessel
      : null;
  const strawColor =
    location.strawColor === "blue" ||
    location.strawColor === "yellow" ||
    location.strawColor === "white" ||
    location.strawColor === "red" ||
    location.strawColor === "green" ||
    location.strawColor === "purple"
      ? location.strawColor
      : null;
  const brand =
    location.brand === "kitazato" ||
    location.brand === "cooper_surgical" ||
    location.brand === "vitrolife" ||
    location.brand === "cryotech"
      ? location.brand
      : null;
  const vitrificationSystem =
    location.vitrificationSystem === "open" ||
    location.vitrificationSystem === "closed"
      ? location.vitrificationSystem
      : null;

  return {
    id: location.id,
    siteEntityId: location.siteEntityId ?? undefined,
    custodianEntityId: location.custodianEntityId ?? undefined,
    storageAccountId: location.storageAccountId ?? undefined,
    quantity: location.quantity,
    quantityUnit,
    storedFrom: location.storedFrom,
    storedTo: location.storedTo,
    isActive: location.isActive,
    endReason: location.endReason ?? undefined,
    reason: location.reason,
    facility: location.facility ?? undefined,
    storageType,
    storageVessel,
    tankNumber: location.tankNumber,
    canisterNumber: location.canisterNumber,
    caneNumber: location.caneNumber,
    subcanisterNumber: location.subcanisterNumber,
    rackNumber: location.rackNumber,
    strawColor,
    strawNumber: location.strawNumber,
    brand,
    vitrificationSystem,
    incubatorNumber: location.incubatorNumber,
    chamberNumber: location.chamberNumber,
    dishNumber: location.dishNumber,
    dropNumber: location.dropNumber,
    notes: location.notes,
  };
}

function getFieldLabel(
  t: TranslationFunction,
  field: CryoSpecimenEditField,
): string {
  switch (field) {
    case "status":
      return t("cryo_vault.modal.status_label");
    case "notes":
      return t("cryo_vault.modal.your_notes");
    case "sample_id":
      return t("cryo_vault.modal.sample_id");
    case "identification_number":
      return t("cryo_vault.modal.identification_number");
    case "collection_date":
      return t("cryo_vault.modal.collection_date");
    case "freeze_date":
      return t("cryo_vault.modal.freeze_date");
    case "facility":
      return t("cryo_vault.modal.facility");
    case "storage_locator":
      return t("cryo_vault.modal.storage_locator");
  }
}

function getFieldTitleKey(field: CryoSpecimenEditField): string {
  switch (field) {
    case "status":
      return "cryo_vault.modal.edit_status_title";
    case "notes":
      return "cryo_vault.modal.edit_notes_title";
    case "sample_id":
      return "cryo_vault.modal.edit_sample_id_title";
    case "identification_number":
      return "cryo_vault.modal.edit_identification_number_title";
    case "collection_date":
      return "cryo_vault.modal.edit_collection_date_title";
    case "freeze_date":
      return "cryo_vault.modal.edit_freeze_date_title";
    case "facility":
      return "cryo_vault.modal.edit_facility_title";
    case "storage_locator":
      return "cryo_vault.modal.edit_storage_locator_title";
  }
}

function getFieldIcon(field: CryoSpecimenEditField) {
  switch (field) {
    case "status":
      return Shield;
    case "notes":
      return FileText;
    case "sample_id":
    case "identification_number":
      return Hash;
    case "collection_date":
      return CalendarDays;
    case "freeze_date":
      return Snowflake;
    case "facility":
      return MapPin;
    case "storage_locator":
      return Snowflake;
  }
}

export function EditCryoSpecimenFieldDialog({
  visible,
  specimen,
  field,
  onClose,
}: {
  visible: boolean;
  specimen: CryoSpecimenDetail | null;
  field: CryoSpecimenEditField | null;
  onClose: () => void;
}) {
  const t = useTranslations();
  const updateSpecimen = useUpdateSpecimen();
  const updateCohort = useUpdateOocyteCohort();
  const [value, setValue] = useState("");
  const capturePress = usePressCapture("EditCryoSpecimenFieldDialog");
  const [statusValue, setStatusValue] = useState<EditableStatus>("stored");
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!specimen || !field) return;
    setValue(
      field === "notes"
        ? (specimen.notes ?? "")
        : field === "sample_id"
          ? (specimen.sampleId ?? "")
          : field === "identification_number"
            ? (specimen.identificationNumber ?? "")
            : field === "collection_date"
              ? (specimen.collectedAt?.slice(0, 10) ?? "")
              : field === "freeze_date"
                ? (specimen.frozenDate?.slice(0, 10) ?? "")
                : field === "facility"
                  ? specimen.facility
                  : field === "storage_locator"
                    ? formatStorageLocator(specimen)
                    : "",
    );
    setStatusValue(
      STATUS_OPTIONS.find((option) => option === specimen.status) ?? "stored",
    );
    setSaveError(null);
  }, [field, specimen, visible]);

  if (!visible || !specimen || !field) return null;

  const isCohort = specimen.oocyteRecordKind === "cohort";
  const FieldIcon = getFieldIcon(field);
  const fieldLabel = getFieldLabel(t, field);
  const isSaving = updateSpecimen.isPending || updateCohort.isPending;
  const isDateField = field === "collection_date" || field === "freeze_date";
  const isRequiredField =
    field === "facility" ||
    field === "freeze_date" ||
    field === "storage_locator";
  const isValid =
    (!isRequiredField || value.trim().length > 0) &&
    (!isDateField || !value.trim() || /^\d{4}-\d{2}-\d{2}$/.test(value.trim()));

  const handleSave = async () => {
    if (!isValid) return;
    setSaveError(null);

    try {
      if (isCohort) {
        if (field !== "notes") return;
        await updateCohort.mutateAsync({
          cohortId: specimen.id,
          data: { notes: value.trim() || null },
        });
        onClose();
        return;
      }

      if (field === "status") {
        if (specimen.type === "sperm") {
          await updateSpecimen.mutateAsync({
            id: specimen.id,
            type: "semen",
            data: { status: statusValue },
          });
        } else if (specimen.type === "oocyte") {
          await updateSpecimen.mutateAsync({
            id: specimen.id,
            type: "oocyte",
            data: { status: statusValue },
          });
        } else {
          await updateSpecimen.mutateAsync({
            id: specimen.id,
            type: "embryo",
            data: { status: statusValue },
          });
        }
      } else if (field === "notes") {
        const serializedNotes = replaceSerializedField(
          specimen.rawNotes,
          [
            {
              label:
                specimen.type === "oocyte"
                  ? "Retrieval Date"
                  : specimen.type === "sperm"
                    ? "Collection Date"
                    : "Fertilisation Date",
              value: specimen.collectedAt?.slice(0, 10) ?? "",
            },
            {
              label: "Freeze Date",
              value: specimen.frozenDate?.slice(0, 10) ?? "",
            },
            { label: "Clinic", value: specimen.facility },
            { label: "Storage ID", value: formatStorageLocator(specimen) },
          ],
          value,
        );
        if (specimen.type === "sperm") {
          await updateSpecimen.mutateAsync({
            id: specimen.id,
            type: "semen",
            data: { notes: serializedNotes },
          });
        } else if (specimen.type === "oocyte") {
          await updateSpecimen.mutateAsync({
            id: specimen.id,
            type: "oocyte",
            data: { notes: serializedNotes },
          });
        } else {
          await updateSpecimen.mutateAsync({
            id: specimen.id,
            type: "embryo",
            data: { notes: serializedNotes },
          });
        }
      } else if (field === "sample_id" && specimen.type === "sperm") {
        await updateSpecimen.mutateAsync({
          id: specimen.id,
          type: "semen",
          data: { sampleId: value.trim() || null },
        });
      } else if (
        field === "identification_number" &&
        specimen.type === "oocyte"
      ) {
        await updateSpecimen.mutateAsync({
          id: specimen.id,
          type: "oocyte",
          data: { identificationNumber: value.trim() || null },
        });
      } else if (field === "collection_date") {
        const collectedAt = value.trim() || undefined;
        if (specimen.type === "sperm") {
          await updateSpecimen.mutateAsync({
            id: specimen.id,
            type: "semen",
            data: { collectedAt },
          });
        } else if (specimen.type === "oocyte") {
          await updateSpecimen.mutateAsync({
            id: specimen.id,
            type: "oocyte",
            data: { collectedAt },
          });
        } else {
          await updateSpecimen.mutateAsync({
            id: specimen.id,
            type: "embryo",
            data: { collectedAt },
          });
        }
      } else if (
        field === "freeze_date" ||
        field === "facility" ||
        field === "storage_locator"
      ) {
        const storageLocations = buildUpdatedStorageLocations(
          specimen,
          field,
          value,
        );
        if (specimen.type === "sperm") {
          await updateSpecimen.mutateAsync({
            id: specimen.id,
            type: "semen",
            data: { storageLocations },
          });
        } else if (specimen.type === "oocyte") {
          await updateSpecimen.mutateAsync({
            id: specimen.id,
            type: "oocyte",
            data: { storageLocations },
          });
        } else {
          await updateSpecimen.mutateAsync({
            id: specimen.id,
            type: "embryo",
            data: { storageLocations },
          });
        }
      }
      onClose();
    } catch (error) {
      setSaveError(
        error instanceof Error
          ? error.message
          : t("cryo_vault.modal.save_error"),
      );
    }
  };

  const title = t(getFieldTitleKey(field));
  const subtitle = t("cryo_vault.modal.focused_edit_body");

  return (
    <Dialog
      visible={visible}
      onClose={isSaving ? () => undefined : onClose}
      closeButton="minimal"
      containerStyle={{ width: "90%", maxWidth: 390, maxHeight: 560 }}
      surfaceStyle={{ maxHeight: 560 }}
      contentStyle={{
        paddingTop: 20,
        paddingHorizontal: 20,
        paddingBottom: 18,
        maxHeight: 560,
      }}
    >
      <DialogHeader
        title={title}
        subtitle={subtitle}
        style={{ marginBottom: 16 }}
      />
      <ScrollView
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ gap: 12, paddingBottom: 4 }}
      >
        {field === "status" ? (
          <MoonFormField label={fieldLabel} labelIcon={FieldIcon}>
            <View style={{ gap: 8 }}>
              {STATUS_OPTIONS.map((option) => {
                const selected = statusValue === option;
                return (
                  <Pressable
                    key={option}
                    accessibilityRole="radio"
                    accessibilityState={{ selected }}
                    onPress={() => {
                      capturePress(pressLabel(option), "specimen_status");
                      setStatusValue(option);
                    }}
                    style={{
                      minHeight: 44,
                      justifyContent: "center",
                      borderRadius: 10,
                      borderWidth: 1,
                      borderColor: selected
                        ? EMBER.colors.textLight
                        : "rgba(92,52,32,0.14)",
                      backgroundColor: selected
                        ? "rgba(92,52,32,0.1)"
                        : "rgba(255,255,255,0.6)",
                      paddingHorizontal: 12,
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        color: EMBER.colors.text,
                        fontSize: 14,
                        fontWeight: selected ? "700" : "500",
                      }}
                    >
                      {getStatusLabel(t, option)}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </MoonFormField>
        ) : field === "collection_date" || field === "freeze_date" ? (
          <MoonDateField
            label={fieldLabel}
            labelIcon={FieldIcon}
            value={value}
            onChange={setValue}
            placeholder={t("cryo_vault.modal.record_date_placeholder")}
          />
        ) : (
          <MoonFormField label={fieldLabel} labelIcon={FieldIcon}>
            <MoonFieldInput paddingY={field === "notes" ? 10 : 0}>
              <TextInput
                accessibilityLabel={fieldLabel}
                value={value}
                onChangeText={setValue}
                placeholder={
                  field === "notes"
                    ? t("cryo_vault.modal.notes_placeholder")
                    : field === "sample_id"
                      ? t("cryo_vault.modal.sample_id_placeholder")
                      : field === "identification_number"
                        ? t(
                            "cryo_vault.modal.identification_number_placeholder",
                          )
                        : field === "facility"
                          ? t("cryo_vault.modal.facility_placeholder")
                          : t("cryo_vault.modal.storage_locator_placeholder")
                }
                placeholderTextColor={EMBER.colors.textFaint}
                multiline={field === "notes"}
                style={{
                  minHeight: field === "notes" ? 88 : 44,
                  textAlignVertical: field === "notes" ? "top" : "center",
                  paddingVertical: field === "notes" ? 0 : 10,
                  color: EMBER.colors.text,
                  fontSize: 14,
                }}
              />
            </MoonFieldInput>
          </MoonFormField>
        )}
        {!isValid && (
          <Text
            accessibilityRole="alert"
            style={{ color: "#B42318", fontSize: 12, lineHeight: 17 }}
          >
            {isDateField
              ? t("cryo_vault.modal.record_date_invalid")
              : t("cryo_vault.modal.focused_edit_required")}
          </Text>
        )}
        {saveError && (
          <Text
            accessibilityRole="alert"
            style={{ color: "#B42318", fontSize: 12, lineHeight: 17 }}
          >
            {saveError}
          </Text>
        )}
      </ScrollView>
      <View
        style={{
          flexDirection: "row",
          gap: 8,
          borderTopWidth: 1,
          borderTopColor: "rgba(92,52,32,0.08)",
          paddingTop: 14,
          marginTop: 16,
        }}
      >
        <DialogSecondaryButton
          flex={1}
          label={t("appointment_detail.cancel")}
          onPress={onClose}
          disabled={isSaving}
        />
        <DialogPrimaryButton
          flex={1.35}
          label={
            isSaving
              ? t("cryo_vault.modal.saving_changes")
              : t("cryo_vault.modal.save_changes")
          }
          onPress={handleSave}
          disabled={isSaving || !isValid}
          icon={
            isSaving ? (
              <ActivityIndicator color={EMBER.colors.textLight} size="small" />
            ) : undefined
          }
        />
      </View>
    </Dialog>
  );
}
